import RBAC                   from '@pixul/hestia-rbac';
import { useUserStore }       from '@/store/user';
import { dynamicPermissions } from '@/utils/permissions/dynamicPermissions';

export class PermissionManager {

  /**
   * @description Simulate actual user having no roles. Can not be modified with code.
   * @type {boolean}
   * @static
   * @private
   */
  static simulateNoRoles = false;
  /**
   * @description Simulate actual user having a specific role. Can not be modified with code
   * @type {?string}
   * @static
   * @private
   */
  static simulateActualRole = undefined;

  /**
   * @description Roles allowed to do everything in Comms
   * @type {string[]}
   * @static
   * @public
   */
  static adminRoles = [RBAC.ROLE_SUPER_ADMIN, RBAC.ROLE_ADMIN_COMMISSION];

  /**
   * @description Return true or false if the user has the correct roles for the requested action, always return true for admin roles
   * @param {?permissionElement|string[]} permissionElement
   * @returns {!boolean}
   * @public
   * @static
   * @throws PermissionParameterError
   */
  static getPermissionForElement(permissionElement) {

    const store = useUserStore();
    const actualRoles = store?.user?.roles || [];
    const overrideRoles = store?.overrideRoles?.value;

    if (!Array.isArray(permissionElement) && permissionElement !== null) {

      throw new PermissionParameterError(`permissionElement passed to PermissionManager is not a valid element ${ permissionElement }`);
    }

    if (this.simulateNoRoles) {
      return false;
    }

    if (this.simulateActualRole) {
      if (permissionElement === null) {
        return RBAC.can([this.simulateActualRole], [...this.adminRoles]);
      }

      return RBAC.can([this.simulateActualRole], [...permissionElement, ...this.adminRoles]);
    }

    if (overrideRoles && overrideRoles.length > 0) {

      if (permissionElement === null || permissionElement.includes(...this.adminRoles)) {
        return RBAC.can(actualRoles, [...this.adminRoles]);
      }

      return RBAC.can(overrideRoles, [...permissionElement, ...this.adminRoles]);
    }

    if (permissionElement === null) {
      return RBAC.can(actualRoles, this.adminRoles);
    }

    //Element has no defined permissions so we allow everyone
    if (Array.isArray(permissionElement) && permissionElement.length === 0) {
      return true;
    }

    return RBAC.can(actualRoles, [...permissionElement, ...this.adminRoles]);
  }

  /**
   * @description Set allowed roles for specific action, if the action is only for admins use null instead of Array, empty array for all roles
   * @typedef permissionElement
   * @readonly
   * @enum
   * @public
   * @static
   */
  static permissionElement = {
    admin             : {
      show              : [RBAC.ROLE_CONTRACT, RBAC.ROLE_PROFORMA, RBAC.ROLE_ACCOUNTING, RBAC.ROLE_SUPER_ADMIN, RBAC.ROLE_ADMIN_COMMISSION],
      taskTemplates     : [RBAC.ROLE_CONTRACT],
      proformaTemplates : [RBAC.ROLE_PROFORMA],
      linkTemplates     : [RBAC.ROLE_CONTRACT],
      notaryTemplates   : [RBAC.ROLE_ACCOUNTING],
      relations         : [RBAC.ROLE_PROFORMA, RBAC.ROLE_SUPER_ADMIN, RBAC.ROLE_ADMIN_COMMISSION],
      partners          : [RBAC.ROLE_PROFORMA],
      banks             : [RBAC.ROLE_ACCOUNTING],
      permissions       : null
    },
    dashboard         : {
      mandate  : null,
      sale     : null,
      turnover : [RBAC.ROLE_SST_MEMBER, RBAC.ROLE_VEFA_MEMBER],
      offers   : [RBAC.ROLE_SST_MEMBER, RBAC.ROLE_VEFA_MEMBER],
      dossiers : [RBAC.ROLE_SST_MEMBER, RBAC.ROLE_VEFA_MEMBER],
      actes    : [RBAC.ROLE_SST_MEMBER, RBAC.ROLE_VEFA_MEMBER]
    },
    dossiers          : {
      filters           : {
        commune                : [],
        status                 : [],
        seller                 : [],
        notary                 : [],
        price                  : [],
        assigned               : [],
        cdv                    : [],
        agents                 : [],
        buyers                 : [],
        exclusive              : [],
        vefa                   : [],
        validationByHeadOffice : []
      },
      actions           : {
        create : [RBAC.ROLE_CONTRACT],
        list   : [],
        resume : [],
        update : [RBAC.ROLE_CONTRACT]
      },
      informations      : {
        canHideFolder                : null,
        createLitige                 : [RBAC.ROLE_LITIGATION],
        internalNote                 : [RBAC.ROLE_PROFORMA, RBAC.ROLE_CONTRACT, RBAC.ROLE_ACCOUNTING],
        openUpdateMenu               : [RBAC.ROLE_CONTRACT],
        previewCover                 : [RBAC.ROLE_CONTRACT],
        updateActDate                : [RBAC.ROLE_CONTRACT],
        updatePartnerDelegations     : [RBAC.ROLE_CONTRACT, RBAC.ROLE_PROFORMA],
        updatedAssigned              : [RBAC.ROLE_CONTRACT],
        updateBuyers                 : [RBAC.ROLE_CONTRACT],
        updateCadastre               : null,
        updateCDV                    : [RBAC.ROLE_CONTRACT],
        updateComment                : [RBAC.ROLE_CONTRACT],
        updateValidationByHeadOffice : [RBAC.ROLE_PROFORMA, RBAC.ROLE_SUPER_ADMIN, RBAC.ROLE_ADMIN_COMMISSION],
        updateCommune                : null,
        updateFees                   : dynamicPermissions.updateFees,
        updateFurnitures             : [RBAC.ROLE_CONTRACT],
        updateFeesPayableBy          : [RBAC.ROLE_CONTRACT],
        updateMandate                : [RBAC.ROLE_CONTRACT],
        updateMandateAgents          : null,
        updateMortage                : [RBAC.ROLE_CONTRACT],
        updateName                   : [RBAC.ROLE_CONTRACT],
        updateOffer                  : null,
        updateOwner                  : null,
        updatePrice                  : [RBAC.ROLE_CONTRACT],
        updateProperty               : null,
        updateSellers                : [RBAC.ROLE_CONTRACT],
        updateSellerAgents           : [RBAC.ROLE_CONTRACT],
        updateStatus                 : [RBAC.ROLE_CONTRACT],
        seeOwner                     : [RBAC.ROLE_PROFORMA, RBAC.ROLE_CONTRACT, RBAC.ROLE_ACCOUNTING, RBAC.ROLE_HEAD_OFFICE],
        showLitigeHistory            : [RBAC.ROLE_LITIGATION],
        showHideFolder               : [RBAC.ROLE_ACCOUNTING, RBAC.ROLE_PROFORMA]
      },
      notary            : {
        update : [RBAC.ROLE_CONTRACT, RBAC.ROLE_ACCOUNTING]
      },
      partners          : {
        showInfos               : dynamicPermissions.showPartnerInfos,
        updateDiag              : [RBAC.ROLE_PROFORMA, RBAC.ROLE_ACCOUNTING],
        updateOthers            : [RBAC.ROLE_PROFORMA],
        diagnosticReimbursement : {
          uploadRib       : dynamicPermissions.uploadRib,
          downloadRib     : [RBAC.ROLE_ACCOUNTING],
          previewRib      : [RBAC.ROLE_ACCOUNTING],
          uploadInvoice    : dynamicPermissions.uploadDiagnosticReimbursmentInvoice,
          downloadInvoice  : dynamicPermissions.uploadDiagnosticReimbursmentInvoice,
          previewInvoice   : dynamicPermissions.uploadDiagnosticReimbursmentInvoice
        }
      },
      tasks             : {
        show   : dynamicPermissions.showTask,
        create : dynamicPermissions.createTask,
        update : dynamicPermissions.updateTaskInfos
      },
      documents         : {
        show           : dynamicPermissions.updateDocuments,
        download       : dynamicPermissions.updateDocuments,
        upload         : dynamicPermissions.updateDocuments,
        deleteFile     : [RBAC.ROLE_CONTRACT],
        createFolder   : dynamicPermissions.updateDocuments,
        copyUploadLink : dynamicPermissions.sendUploadLink,
        sendUploadLink : dynamicPermissions.sendUploadLink,
        sendToNotary   : dynamicPermissions.sendToNotary
      },
      commissions       : {
        generalParameters   : {
          calculDate               : [],
          commissionsNote          : [RBAC.ROLE_PROFORMA, RBAC.ROLE_ACCOUNTING],
          show                     : dynamicPermissions.showCommissionsGeneralParameters,
          updateInfos              : [RBAC.ROLE_PROFORMA],
          updateFees               : [RBAC.ROLE_PROFORMA],
          updateDiag               : [RBAC.ROLE_PROFORMA],
          updateBuyerPartnerLead   : [RBAC.ROLE_PROFORMA],
          updateMandatePartnerLead : [RBAC.ROLE_PROFORMA],
          updateBaremeType         : [RBAC.ROLE_PROFORMA],
          updateCoastal            : [RBAC.ROLE_PROFORMA]
        },
        actorParameters     : {
          show              : dynamicPermissions.showActorsParameters,
          updateInfos       : [RBAC.ROLE_PROFORMA],
          checkboxFollowup : null
        },
        conflicts           : {
          show : [RBAC.ROLE_PROFORMA, RBAC.ROLE_ACCOUNTING]
        },
        commissionBreakdown : {
          show                  : dynamicPermissions.showCommissionBreakdown,
          seeAgentProfile       : [RBAC.ROLE_PROFORMA, RBAC.ROLE_ACCOUNTING],
          updateProformaStatus  : dynamicPermissions.changeCommissionProformaStatus,
          updateInvoiceStatus   : [RBAC.ROLE_ACCOUNTING],
          uploadInvoice         : dynamicPermissions.uploadInvoice,
          updateInfos           : [RBAC.ROLE_PROFORMA],
          updateDetails         : null,
          addProforma           : [RBAC.ROLE_ACCOUNTING, RBAC.ROLE_PROFORMA],
          validate              : [RBAC.ROLE_PROFORMA],
          showTotal             : [RBAC.ROLE_SALE_MEMBER, RBAC.ROLE_ACCOUNTING, RBAC.ROLE_PROFORMA],
          updateAgentValidation : dynamicPermissions.changeCommissionAgentValidation,
          showRelations         : [RBAC.ROLE_PROFORMA, RBAC.ROLE_CONTRACT],
          reset                 : [RBAC.ROLE_PROFORMA]
        }
      },
      thirdPartyInvoice : {
        createInvoice          : [RBAC.ROLE_ACCOUNTING, RBAC.ROLE_PROFORMA],
        showInvoice            : dynamicPermissions.thirdPartyInvoiceIfParticipantOrVefa,
        updateInvoice          : dynamicPermissions.thirdPartyInvoiceIfParticipant,
        validateInvoice        : dynamicPermissions.thirdPartyInvoiceIfParticipant,
        downloadInvoice        : dynamicPermissions.thirdPartyInvoiceIfParticipantOrVefa,
        createNotaryInvoice    : [RBAC.ROLE_ACCOUNTING, RBAC.ROLE_PROFORMA],
        updateNotaryInvoice    : [RBAC.ROLE_ACCOUNTING, RBAC.ROLE_PROFORMA],
        updateFundsReceiptDate : [RBAC.ROLE_ACCOUNTING]
      },
      alerts            : {
        showAlerts : [RBAC.ROLE_CONTRACT]
      },
      sandbox           : {
        show : null
      }
    },
    exception         : {
      show : [RBAC.ROLE_PROFORMA, RBAC.ROLE_CONTRACT, RBAC.ROLE_ACCOUNTING]
    },
    invoice           : {
      show : [RBAC.ROLE_ACCOUNTING]
    },
    notary            : {
      actions : {
        list   : [],
        edit   : [RBAC.ROLE_CONTRACT],
        delete : [RBAC.ROLE_CONTRACT],
        create : [RBAC.ROLE_CONTRACT]
      }
    },
    proforma          : {
      show                   : [RBAC.ROLE_PROFORMA, RBAC.ROLE_ACCOUNTING],
      showProformasToInvoice : [RBAC.ROLE_PROFORMA]
    },
    thirdPartyInvoice : {
      showInvoicesToBeCreated : [RBAC.ROLE_ACCOUNTING]
    },
    offer             : {
      show : [RBAC.ROLE_CONTRACT, RBAC.ROLE_PROFORMA, RBAC.ROLE_ACCOUNTING]
    }
  };
}

class PermissionParameterError extends Error {
}
